/**
 * The ONE translation between the Limits form and the stored `discountGovernance` block
 * (MAR-311).
 *
 * ⚠️ THIS FILE HAS NO DEPENDENCIES ON PURPOSE. The web app bundles it into PRODUCT code,
 * the migration script and the E2E suite call it, and `discount-governance` reads
 * `normalizeProductId` on the mint path. A constant borrowed from a client-building module
 * costs every one of those consumers a client. Keep it free of `require`.
 *
 * WHY IT EXISTS. The form saved two safeguards under keys the enforcer never read:
 *
 *   - the per-customer limit went to `budget.perCustomerLimit` / `budget.perCustomerPeriod`,
 *     while `checkCustomerLimit` reads `customerLimits.{maxPerCustomer,period}` and treats
 *     an absent `customerLimits` as NO limit at all;
 *   - product exclusions went to a top-level `exclusions`, while `checkProductExclusions`
 *     reads `productExclusions`.
 *
 * Every dev workspace showed "3 per customer, monthly" and enforced nothing. The form also
 * rebuilt the whole block from its own fields, so any key it did not know was wiped on
 * save. Both directions now go through here, so the form, the migration and the E2E
 * fixture cannot drift from the shape the enforcer reads.
 *
 * THE CONTRACT
 *
 *   stored (what `PUT /workspaces` persists, what the enforcer reads):
 *     customerLimits:    { maxPerCustomer, period }            period ∈ PER_CUSTOMER_PERIODS
 *     productExclusions: { productIds, categories: [], tags: [] }
 *
 *   form values: every stored key passed through, legacy keys dropped, plus
 *     customerLimits:    { maxPerCustomer, period }
 *     productExclusions: { productIds }
 *
 * Category and tag exclusions are written EMPTY: no cart item that reaches the enforcer
 * carries a category or tags, so a configured one could never match. They come back with
 * server-side enrichment (follow-up), not as a field that promises a safeguard it can't keep.
 *
 * @module @marva/shared/discount-governance-form
 */

'use strict';

/**
 * The periods `getPeriodStart` (discount-governance) actually distinguishes. It has no
 * `daily` case — a daily period fell through to its monthly default — so offering `daily`
 * was a promise the counter never kept.
 */
const PER_CUSTOMER_PERIODS = Object.freeze(['weekly', 'monthly', 'yearly']);

/**
 * What an unsupported period becomes. `monthly`, because that is what the enforcer already
 * did with one (`getPeriodStart`'s default branch): mapping legacy `daily` here changes the
 * label, never the behaviour.
 */
const FALLBACK_PER_CUSTOMER_PERIOD = 'monthly';

/**
 * The limit the form has always displayed for a workspace that stored none. A row with
 * neither the new nor the legacy key reads as this, so what the merchant sees is what a
 * save (or the migration) writes.
 */
const DEFAULT_CUSTOMER_LIMITS = Object.freeze({ maxPerCustomer: 3, period: 'monthly' });

const SHOPIFY_PRODUCT_GID = /^gid:\/\/shopify\/Product\//i;

/**
 * A product id in the one form both sides compare: a trimmed string with any
 * `gid://shopify/Product/` prefix removed. Cart items arrive embed-shaped (`productId`, a
 * numeric string) or webhook-shaped (`product_id`, a number), and a merchant may paste
 * either the bare id or the GID — all of them must meet as the same string.
 *
 * Anything that is not a string or a finite number becomes `''`, which callers treat as
 * "no id" and must never let match.
 *
 * @param {unknown} value
 * @returns {string}
 */
function normalizeProductId(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '';
  if (typeof value !== 'string') return '';
  return value.trim().replace(SHOPIFY_PRODUCT_GID, '').trim();
}

/** Normalised, non-empty, de-duplicated, order kept. A non-array reads as empty. */
function normalizeProductIds(values) {
  if (!Array.isArray(values)) return [];
  const seen = new Set();
  for (const value of values) {
    const id = normalizeProductId(value);
    if (id) seen.add(id);
  }
  return [...seen];
}

function normalizePeriod(period) {
  return PER_CUSTOMER_PERIODS.includes(period) ? period : FALLBACK_PER_CUSTOMER_PERIOD;
}

/**
 * A positive whole number. The schema requires `int().positive()`.
 *
 * A finite value below 1 clamps to 1 — the TIGHTEST limit, and what the enforcer already
 * applies to a stored 0 (`maxPerCustomer || 1`). Falling back to the display default here
 * would quietly loosen a limit the merchant set. Only a non-number takes the default.
 */
function normalizeMaxPerCustomer(value) {
  const n = Math.floor(Number(value));
  if (!Number.isFinite(n)) return DEFAULT_CUSTOMER_LIMITS.maxPerCustomer;
  return Math.max(1, n);
}

function isObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/** `budget` without the two legacy per-customer keys; absent stays absent. */
function budgetWithoutLegacy(budget) {
  if (!isObject(budget)) return budget;
  const { perCustomerLimit: _limit, perCustomerPeriod: _period, ...rest } = budget;
  return rest;
}

/**
 * Stored block → form values.
 *
 * Reads `customerLimits` / `productExclusions` first and falls back to the legacy
 * `budget.perCustomerLimit` / `exclusions`, so a row not yet migrated still shows the
 * merchant what they saw before. Every other key passes through untouched.
 *
 * @param {object|null|undefined} stored  `workspace.discountGovernance`
 * @returns {object} form values (see the module header)
 */
function fromStoredGovernance(stored) {
  const dg = isObject(stored) ? stored : {};
  const { exclusions: legacyExclusions, ...rest } = dg;
  const legacyBudget = isObject(dg.budget) ? dg.budget : {};

  const limits = isObject(dg.customerLimits)
    ? dg.customerLimits
    : legacyBudget.perCustomerLimit !== undefined || legacyBudget.perCustomerPeriod !== undefined
      ? { maxPerCustomer: legacyBudget.perCustomerLimit, period: legacyBudget.perCustomerPeriod }
      : DEFAULT_CUSTOMER_LIMITS;

  const productIds = isObject(dg.productExclusions)
    ? dg.productExclusions.productIds
    : isObject(legacyExclusions) ? legacyExclusions.productIds : [];

  const form = {
    ...rest,
    customerLimits: {
      maxPerCustomer: normalizeMaxPerCustomer(limits.maxPerCustomer),
      period: normalizePeriod(limits.period)
    },
    productExclusions: { productIds: normalizeProductIds(productIds) }
  };
  if ('budget' in dg) form.budget = budgetWithoutLegacy(dg.budget);
  return form;
}

/**
 * Form values → the block to persist.
 *
 * `stored` is spread FIRST, so a key the form does not know survives the save. The
 * safeguards are written under the keys the enforcer reads, and the legacy keys are
 * deleted so no row carries two answers to one question.
 *
 * @param {object} form    form values (from `fromStoredGovernance`, then edited)
 * @param {object} [stored] the current `workspace.discountGovernance`
 * @returns {object}
 */
function toStoredGovernance(form, stored) {
  const base = isObject(stored) ? stored : {};
  const values = isObject(form) ? form : {};
  const limits = isObject(values.customerLimits) ? values.customerLimits : DEFAULT_CUSTOMER_LIMITS;
  const exclusions = isObject(values.productExclusions) ? values.productExclusions : {};

  const next = {
    ...base,
    ...values,
    customerLimits: {
      maxPerCustomer: normalizeMaxPerCustomer(limits.maxPerCustomer),
      period: normalizePeriod(limits.period)
    },
    productExclusions: {
      productIds: normalizeProductIds(exclusions.productIds),
      categories: [],
      tags: []
    }
  };
  delete next.exclusions;
  if ('budget' in next) next.budget = budgetWithoutLegacy(next.budget);
  return next;
}

module.exports = {
  PER_CUSTOMER_PERIODS,
  FALLBACK_PER_CUSTOMER_PERIOD,
  DEFAULT_CUSTOMER_LIMITS,
  normalizeProductId,
  fromStoredGovernance,
  toStoredGovernance
};
